import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { CheckCircle2, Circle, X, Phone, FileText, Smile, ChevronRight } from "lucide-react";

const DISMISS_KEY = "ep_get_started_dismissed_v1";

export default function GetStartedChecklist({ onVisibility }) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(DISMISS_KEY));
  const [formsDone, setFormsDone] = useState(null);
  const [checkinDone, setCheckinDone] = useState(null);
  const phoneDone = !!(user?.phone || "").trim();

  useEffect(() => {
    if (!user || user.role === "admin" || dismissed) return;
    API.get("/intakeq/forms").then(({ data }) => {
      const forms = data?.forms || [];
      setFormsDone(forms.length === 0 ? true : forms.every(f => (f.status || "").toLowerCase() === "completed"));
    }).catch(() => setFormsDone(true));
    API.get("/daily-check-ins").then(({ data }) => setCheckinDone((data || []).length > 0)).catch(() => setCheckinDone(true));
  }, [user, dismissed]);

  const loaded = formsDone !== null && checkinDone !== null;
  const allDone = loaded && phoneDone && formsDone && checkinDone;
  const visible = !!user && user.role !== "admin" && !dismissed && loaded && !allDone;

  useEffect(() => { onVisibility?.(visible); }, [visible, onVisibility]);

  if (!visible) return null;

  const steps = [
    {
      key: "phone", done: phoneDone, icon: Phone,
      title: "Add your phone number",
      hint: "So your care team can reach you by SMS",
      to: "/profile",
    },
    {
      key: "forms", done: formsDone, icon: FileText,
      title: "Complete your intake forms",
      hint: "Your provider reviews these before your visit",
      to: "/forms",
    },
    {
      key: "checkin", done: checkinDone, icon: Smile,
      title: "Log your first daily check-in",
      hint: "One tap below — it builds your Progress trends",
      onClick: () => window.dispatchEvent(new CustomEvent("checkin:open")),
    },
  ];
  const doneCount = steps.filter(s => s.done).length;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid="get-started-checklist">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-display font-semibold text-base">Get started</p>
          <p className="text-xs text-muted-foreground mt-0.5">{doneCount} of {steps.length} done</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {steps.map(s => (
              <span key={s.key} className={`h-1.5 w-6 rounded-full ${s.done ? "bg-sage" : "bg-muted"}`} />
            ))}
          </div>
          <button onClick={dismiss} aria-label="Dismiss checklist" className="text-muted-foreground hover:text-foreground p-1" data-testid="get-started-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="space-y-1">
        {steps.map(step => {
          const inner = (
            <>
              {step.done
                ? <CheckCircle2 className="w-5 h-5 text-sage shrink-0" />
                : <Circle className="w-5 h-5 text-muted-foreground/50 shrink-0" />}
              <div className="flex-1 min-w-0 text-left">
                <p className={`text-sm font-medium ${step.done ? "line-through text-muted-foreground" : ""}`}>{step.title}</p>
                {!step.done && <p className="text-[11px] text-muted-foreground">{step.hint}</p>}
              </div>
              {!step.done && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
            </>
          );
          const cls = "flex items-center gap-3 py-2 px-2 rounded-lg w-full " + (step.done ? "" : "hover:bg-accent transition-colors");
          if (step.done) return <div key={step.key} className={cls} data-testid={`get-started-step-${step.key}`}>{inner}</div>;
          if (step.to) return <Link key={step.key} to={step.to} className={cls} data-testid={`get-started-step-${step.key}`}>{inner}</Link>;
          return <button key={step.key} onClick={step.onClick} className={cls} data-testid={`get-started-step-${step.key}`}>{inner}</button>;
        })}
      </div>
    </div>
  );
}
